import { ReactNode, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { OUTLINE_BUTTON_CLASS } from "components/FlowButtons";
import { NetworkError, usePOST } from "hooks/usePublicAPI";
import {
  FIRST_CALL_PHONE_HREF,
  FIRST_CALL_PHONE_LABEL,
} from "constants/phones";

import { Frame } from "pages/QuoteFlow/CaseQuoteFrame";
import QuoteFlowLoadingPage from "pages/QuoteFlow/QuoteFlowLoadingPage";
import { takeLeadReturn } from "pages/StartLead/leadReturn";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { leadFlowEvents } from "pages/StartLead/events";
import { clearStoredState } from "pages/StartLead/state";

import {
  confirmFailure,
  resumeDestination,
  resumeFailure,
  resumeOutcome,
  submissionIdFromSearch,
  isReminderLink,
  type ConfirmResponse,
  type ResumeFailure,
} from "./resume";

function Shell({ children }: { children: ReactNode }) {
  return (
    <Frame shell="standalone">
      <main className="flex flex-1 items-center justify-center px-4 py-16">
        {children}
      </main>
    </Frame>
  );
}

function FailureNotice({
  failure,
  onRetry,
}: {
  failure: ResumeFailure;
  onRetry: () => void;
}) {
  return (
    <div className="mx-auto flex max-w-[560px] flex-col items-center gap-5 rounded-2xl border border-oat-200 bg-white p-8 text-center">
      <h1 className="font-serif text-[31px] leading-tight text-soil">
        {failure === "not_confirmed"
          ? "We couldn't finish this on your account"
          : "Something went wrong"}
      </h1>
      <p className="text-lg leading-relaxed text-oat-700">
        {failure === "not_confirmed"
          ? "Please call us and we'll pick it up with you."
          : "Please try again, or call us and we'll take it from here."}
      </p>
      <p className="text-base text-soil">
        Call us at{" "}
        <a href={FIRST_CALL_PHONE_HREF} className="font-bold underline">
          {FIRST_CALL_PHONE_LABEL}
        </a>
        . We&rsquo;re here 24/7.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {failure === "error" && (
          <button
            type="button"
            className={CONTINUE_BUTTON_CLASS}
            onClick={onRetry}
          >
            Try again
          </button>
        )}
        <Link to="/" className={OUTLINE_BUTTON_CLASS}>
          Go to your account
        </Link>
      </div>
    </div>
  );
}

/**
 * Where a confirmed lead sign-in lands. Confirms the pending submission on
 * the signed-in account, then goes straight to the quote or case it made.
 * The server checks the signed-in person against the proof for this
 * submission; a refusal is shown as one message with our phone number,
 * never a blank page.
 */
export default function StartResume() {
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackLeadFlowEvent();
  const submissionId = submissionIdFromSearch(location.search);
  const [failure, setFailure] = useState<ResumeFailure | null>(null);
  const [attempt, setAttempt] = useState(0);
  const startedAttempt = useRef(-1);
  const reminderReported = useRef(false);
  const fromReminder = isReminderLink(location.search);

  useEffect(() => {
    if (!fromReminder || reminderReported.current) return;
    reminderReported.current = true;
    trackEvent(leadFlowEvents.reminderLinkOpened());
  }, [fromReminder, trackEvent]);

  // The credentialed client: the confirm answer sets the remembered-browser
  // and quote-session cookies on the API's origin.
  const { trigger } = usePOST(
    `/leads/submissions/${submissionId ?? "none"}/confirm`,
  );

  useEffect(() => {
    if (!submissionId || startedAttempt.current === attempt) return;
    startedAttempt.current = attempt;
    trigger()
      .then((response: ConfirmResponse) => {
        const unfinished = confirmFailure(response);
        if (unfinished) {
          trackEvent(leadFlowEvents.confirmCompleted("incomplete"));
          setFailure(unfinished);
          return;
        }
        trackEvent(
          leadFlowEvents.confirmCompleted(resumeOutcome(response.next)),
        );
        clearStoredState();
        navigate(
          resumeDestination(response.next, takeLeadReturn(submissionId)),
          {
            replace: true,
          },
        );
      })
      .catch((err: unknown) => {
        const statusCode =
          err instanceof NetworkError ? err.statusCode : undefined;
        const kind = resumeFailure(statusCode);
        if (!(err instanceof NetworkError)) Sentry.captureException(err);
        trackEvent(leadFlowEvents.confirmCompleted(kind));
        setFailure(kind);
      });
  }, [submissionId, attempt, trigger, trackEvent, navigate]);

  if (!submissionId) {
    return <Navigate to="/" replace />;
  }

  if (failure) {
    return (
      <Shell>
        <FailureNotice
          failure={failure}
          onRetry={() => {
            setFailure(null);
            setAttempt((n) => n + 1);
          }}
        />
      </Shell>
    );
  }

  return <QuoteFlowLoadingPage />;
}
